/**
 * ============================================================================
 * USE NOTIFICATIONS HOOK
 * ============================================================================
 * 
 * Custom hook for notification management
 */

import { useState, useEffect, useCallback } from 'react';
import * as notificationService from '../services/notifications';
import useNotificationStore from '../store/notificationStore';
import useMounted from './useMounted';
import useAbortController from './useAbortController';

const normalizeList = (list) =>
  (Array.isArray(list) ? list : []).map((n) => ({
    ...n,
    read: n.read ?? n.isRead ?? false,
  }));

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const isMounted = useMounted();
  const { signal, abort } = useAbortController();

  /**
   * Load notifications and unread count on mount
   */
  useEffect(() => {
    loadNotifications();
    loadUnreadCount();

    return () => {
      abort(); // Cancel pending requests on unmount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run on mount

  /**
   * Get notifications
   */
  const loadNotifications = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificationService.getNotifications(filters);
      const list = Array.isArray(response?.data) ? response.data : (response?.notifications ?? []);
      
      // Only update state if component is still mounted
      if (isMounted()) {
        setNotifications(list);
      }
      
      return response;
    } catch (err) {
      // Don't update state if component unmounted or request aborted
      if (!isMounted() || err.name === 'AbortError') {
        return;
      }
      setError(err.message || 'Failed to load notifications');
      throw err;
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted]);

  /**
   * Get unread count
   */
  const loadUnreadCount = useCallback(async () => {
    try {
      const count = await notificationService.getUnreadCount();
      const value = typeof count === 'number' ? count : (count?.count ?? count?.data?.count ?? 0);

      if (isMounted()) {
        setUnreadCount(value);
        try {
          useNotificationStore.getState().setUnreadCount(value);
        } catch (_) {}
      }

      return value;
    } catch (err) {
      if (!isMounted() || err.name === 'AbortError') {
        return 0;
      }
      console.error('Failed to load unread count:', err);
      if (isMounted()) {
        setUnreadCount(0);
        try {
          useNotificationStore.getState().setUnreadCount(0);
        } catch (_) {}
      }
      return 0;
    }
  }, [isMounted]);

  /**
   * Get notification by ID
   */
  const getNotification = useCallback(async (notificationId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificationService.getNotification(notificationId);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get notification');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Mark as read (with optimistic update)
   */
  const markAsRead = useCallback(async (notificationId) => {
    // Optimistic update
    const previousNotifications = [...notifications];
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      setLoading(true);
      setError(null);
      await notificationService.markAsRead(notificationId);
      await loadUnreadCount();
    } catch (err) {
      // Rollback on error
      setNotifications(previousNotifications);
      setUnreadCount(prev => prev + 1);
      setError(err.message || 'Failed to mark as read');
      throw err;
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [notifications, loadUnreadCount, isMounted]);

  /**
   * Mark as unread
   */
  const markAsUnread = useCallback(async (notificationId) => {
    try {
      setLoading(true);
      setError(null);
      await notificationService.markAsUnread(notificationId);
      
      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: false } : n)
      );
      await loadUnreadCount();
    } catch (err) {
      setError(err.message || 'Failed to mark as unread');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadUnreadCount]);

  /**
   * Mark all as read
   */
  const markAllAsRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      useNotificationStore.getState().markAllAsRead();
    } catch (_) {}

    try {
      setLoading(true);
      setError(null);
      await notificationService.markAllAsRead();
    } catch (err) {
      setError(err.message || 'Failed to mark all as read');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete notification
   */
  const deleteNotification = useCallback(async (notificationId) => {
    const previous = notifications.filter(n => n.id !== notificationId);
    setNotifications(previous);
    try {
      useNotificationStore.getState().removeNotification(notificationId);
    } catch (_) {}

    try {
      setLoading(true);
      setError(null);
      await notificationService.deleteNotification(notificationId);
      await loadUnreadCount();
    } catch (err) {
      setNotifications(notifications);
      try {
        useNotificationStore.getState().setNotifications(notifications);
      } catch (_) {}
      setError(err.message || 'Failed to delete notification');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [notifications, loadUnreadCount]);

  /**
   * Delete all notifications
   */
  const deleteAllNotifications = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      await notificationService.deleteAllNotifications(filters);
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      setError(err.message || 'Failed to delete all notifications');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get preferences
   */
  const loadPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const preferences = await notificationService.getPreferences();
      setPreferences(preferences);
      return preferences;
    } catch (err) {
      setError(err.message || 'Failed to load preferences');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update preferences
   */
  const updatePreferences = useCallback(async (newPreferences) => {
    try {
      setLoading(true);
      setError(null);
      const updatedPreferences = await notificationService.updatePreferences(newPreferences);
      setPreferences(updatedPreferences);
      return updatedPreferences;
    } catch (err) {
      setError(err.message || 'Failed to update preferences');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Register push token
   */
  const registerPushToken = useCallback(async (token, platform = 'web') => {
    try {
      setLoading(true);
      setError(null);
      await notificationService.registerPushToken(token, platform);
    } catch (err) {
      setError(err.message || 'Failed to register push token');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Unregister push token
   */
  const unregisterPushToken = useCallback(async (token) => {
    try {
      setLoading(true);
      setError(null);
      await notificationService.unregisterPushToken(token);
    } catch (err) {
      setError(err.message || 'Failed to unregister push token');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Subscribe to topic
   */
  const subscribeToTopic = useCallback(async (topic) => {
    try {
      setLoading(true);
      setError(null);
      await notificationService.subscribeToTopic(topic);
    } catch (err) {
      setError(err.message || 'Failed to subscribe to topic');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Unsubscribe from topic
   */
  const unsubscribeFromTopic = useCallback(async (topic) => {
    try {
      setLoading(true);
      setError(null);
      await notificationService.unsubscribeFromTopic(topic);
    } catch (err) {
      setError(err.message || 'Failed to unsubscribe from topic');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get statistics
   */
  const loadStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const stats = await notificationService.getStatistics();
      setStatistics(stats);
      return stats;
    } catch (err) {
      setError(err.message || 'Failed to load statistics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    notifications,
    unreadCount,
    preferences,
    statistics,
    loading,
    error,
    
    // Actions
    loadNotifications,
    loadUnreadCount,
    getNotification,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    loadPreferences,
    updatePreferences,
    registerPushToken,
    unregisterPushToken,
    subscribeToTopic,
    unsubscribeFromTopic,
    loadStatistics,
    
    // Convenience
    refresh: loadNotifications,
  };
};

export default useNotifications;
